// src/features/pedidos/HistorialPedidosPag.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSync,
  FaFilter,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
} from "react-icons/fa";

export default function HistorialPedidosPag() {
  const navigate = useNavigate();

  const [usuarios, setUsuarios] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [tamano, setTamano] = useState(15);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const [filtros, setFiltros] = useState({
    desde: "",
    hasta: "",
    mesero: "",
    mesa: "",
    entregado: "",
    facturado: "",
    cancelado: "",     // 👈 NUEVO
    q: "",
    sort: "fecha,desc",
  });

  const onFiltro = (campo, valor) =>
    setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const cargarUsuarios = async () => {
    try {
      const res = await axios.get("/usuarios");
      setUsuarios(res.data || []);
    } catch (e) {
      console.error("Error cargando usuarios:", e);
    }
  };

  const cargarMesas = async () => {
    try {
      const res = await axios.get("/mesas");
      setMesas(res.data || []);
    } catch (e) {
      console.error("Error cargando mesas:", e);
    }
  };

  const cargarDatos = async (pag = pagina) => {
    setCargando(true);
    try {
      const params = { page: pag, size: tamano, sort: filtros.sort };
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      if (filtros.mesero) params.mesero = filtros.mesero;
      if (filtros.mesa) params.mesa = filtros.mesa;
      if (filtros.entregado !== "")
        params.entregado = filtros.entregado === "true";
      if (filtros.facturado !== "")
        params.facturado = filtros.facturado === "true";
      if (filtros.cancelado !== "")
        params.cancelado = filtros.cancelado === "true";   // 👈 NUEVO
      if (filtros.q) params.q = filtros.q;

      const res = await axios.get("/pedidos/historial", { params });
      const page = res.data;
      setFilas(page.content || []);
      setPagina(page.number);
      setTamano(page.size);
      setTotalPaginas(page.totalPages);
      setTotalRegistros(page.totalElements);
    } catch (e) {
      console.error(e);
      toast.error("Error cargando historial");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
    cargarMesas();
    cargarDatos(0);
  }, []);

  const calcularTotal = (p) =>
    (p?.detalles || []).reduce(
      (acc, d) => acc + (parseFloat(d.subtotal) || 0),
      0
    );

  const aplicarFiltros = () => cargarDatos(0);

  const paginador = useMemo(() => {
    const items = [];
    for (let i = 0; i < totalPaginas; i++) {
      items.push(i);
    }
    return items;
  }, [totalPaginas]);

  const goToPage = (p) => {
    if (p < 0 || p >= totalPaginas) return;
    cargarDatos(p);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="mt-3 bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6">
        {/* Título */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
              Historial de pedidos
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Revisa los pedidos registrados y filtra por fechas, usuario, mesa,
              etc.
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/70 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <FaFilter className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Filtros
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

            {/* Desde */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Desde
              </label>
              <input
                type="datetime-local"
                value={
                  filtros.desde
                    ? new Date(filtros.desde).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  onFiltro(
                    "desde",
                    e.target.value
                      ? new Date(e.target.value).toISOString()
                      : ""
                  )
                }
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              />
            </div>

            {/* Hasta */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Hasta
              </label>
              <input
                type="datetime-local"
                value={
                  filtros.hasta
                    ? new Date(filtros.hasta).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  onFiltro(
                    "hasta",
                    e.target.value
                      ? new Date(e.target.value).toISOString()
                      : ""
                  )
                }
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              />
            </div>

            {/* Usuario (nombre) */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Usuario (nombre)
              </label>
              <select
                value={filtros.mesero}
                onChange={(e) => onFiltro("mesero", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">Todos</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.nombre}>
                    {u.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Mesa */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Mesa
              </label>
              <select
                value={filtros.mesa}
                onChange={(e) => onFiltro("mesa", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">Todas</option>
                {mesas.map((mesa) => (
                  <option key={mesa.id ?? mesa.numero} value={mesa.numero}>
                    Mesa {mesa.numero}
                  </option>
                ))}
              </select>
            </div>

            {/* Entregado */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Entregado
              </label>
              <select
                value={filtros.entregado}
                onChange={(e) => onFiltro("entregado", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>

            {/* Facturado */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Facturado
              </label>
              <select
                value={filtros.facturado}
                onChange={(e) => onFiltro("facturado", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>

            {/* Cancelado — NUEVO */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Cancelado
              </label>
              <select
                value={filtros.cancelado}
                onChange={(e) => onFiltro("cancelado", e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>

            {/* Búsqueda libre */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Búsqueda
              </label>
              <div className="flex rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 overflow-hidden">
                <span className="pl-2 pr-1 flex items-center text-slate-400">
                  <FaSearch className="w-3.5 h-3.5" />
                </span>
                <input
                  placeholder="Descripción, usuario o mesa"
                  value={filtros.q}
                  onChange={(e) => onFiltro("q", e.target.value)}
                  className="flex-1 px-2 py-2 text-xs sm:text-sm bg-transparent text-slate-900 dark:text-slate-100 focus:outline-none"
                />
                <button
                  onClick={aplicarFiltros}
                  className="px-3 py-2 text-xs sm:text-sm font-medium bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"
                >
                  Buscar
                </button>
              </div>
            </div>
          </div>

          {/* Sort + botones */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
            <select
              value={filtros.sort}
              onChange={(e) => onFiltro("sort", e.target.value)}
              className="w-full sm:w-48 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            >
              <option value="fecha,desc">Fecha ↓</option>
              <option value="fecha,asc">Fecha ↑</option>
              <option value="id,desc">ID ↓</option>
              <option value="id,asc">ID ↑</option>
            </select>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => cargarDatos(pagina)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <FaSync className="text-amber-500" />
                Actualizar
              </button>

              <button
                onClick={aplicarFiltros}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                Aplicar filtros
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="mt-3">
          {cargando ? (
            <div className="flex justify-center py-8">
              <div className="w-7 h-7 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800">
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[950px] md:min-w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-950/80">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                        ID
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Fecha
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Mesa
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Usuario
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Descripción
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Total
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Entregado
                      </th>
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Facturado
                      </th>

                      {/* CANCELADO — NUEVO */}
                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Cancelado
                      </th>

                      <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                        Acciones
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filas.length === 0 ? (
                      <tr>
                        <td
                          colSpan="10"
                          className="px-4 py-4 text-center text-slate-500 dark:text-slate-400 text-sm"
                        >
                          Sin resultados
                        </td>
                      </tr>
                    ) : (
                      filas.map((p) => (
                        <tr
                          key={p.id}
                          className={`odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
                            p.cancelado ? "opacity-60" : ""
                          }`}
                        >
                          <td className="px-4 py-2 text-left align-middle font-medium">
                            {p.id}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {new Date(p.fecha).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {p.mesa?.numero ?? "—"}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {p.mesero?.nombre ?? "—"}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {p.descripcion || "—"}
                          </td>
                          <td className="px-4 py-2 text-center align-middle font-semibold">
                            ${calcularTotal(p).toFixed(2)}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {p.entregado ? "Sí" : "No"}
                          </td>
                          <td className="px-4 py-2 text-center align-middle">
                            {p.facturado ? "Sí" : "No"}
                          </td>

                          {/* Celda CANCELADO */}
                          <td className="px-4 py-2 text-center align-middle">
                            {p.cancelado ? (
                              <span className="text-red-500 font-semibold">
                                Sí
                              </span>
                            ) : (
                              "No"
                            )}
                          </td>

                          <td className="px-4 py-2 text-center align-middle">
                            <button
                              onClick={() => navigate(`/pedidos/${p.id}`)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-500 text-sky-300 hover:bg-sky-500 hover:text-slate-900 transition-colors"
                            >
                              <FaEye />
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Paginación */}
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            Página {totalPaginas === 0 ? 0 : pagina + 1} de {totalPaginas} —{" "}
            {totalRegistros} registros
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(pagina - 1)}
              disabled={pagina <= 0}
              className={`px-2.5 py-1.5 rounded-lg border text-xs flex items-center gap-1 ${
                pagina <= 0
                  ? "border-slate-300 text-slate-300 cursor-not-allowed"
                  : "border-slate-400 text-slate-700 dark:border-slate-600 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <FaChevronLeft />
            </button>

            {paginador.map((p) => (
              <button
                key={p}
                onClick={() => goToPage(p)}
                className={`px-2.5 py-1.5 rounded-lg border text-xs ${
                  p === pagina
                    ? "bg-amber-500 border-amber-500 text-white"
                    : "border-slate-400 text-slate-700 dark:border-slate-600 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                {p + 1}
              </button>
            ))}

            <button
              onClick={() => goToPage(pagina + 1)}
              disabled={pagina >= totalPaginas - 1}
              className={`px-2.5 py-1.5 rounded-lg border text-xs flex items-center gap-1 ${
                pagina >= totalPaginas - 1 || totalPaginas === 0
                  ? "border-slate-300 text-slate-300 cursor-not-allowed"
                  : "border-slate-400 text-slate-700 dark:border-slate-600 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
